import React from 'react';
import { CalendarPlus, Download, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../ui/utils';
import type {
  PerformanceGoal,
  PerformanceSchedule,
  PerformanceTouchpoint,
} from '../../types/performance.types';
import { performanceApi } from '../../utils/performance-api';
import { formatPerformanceDate, performancePillClass } from '../../utils/performance';
import { PerformanceEmptyState, PerformancePill } from './PerformancePrimitives';

interface TouchpointsPanelProps {
  touchpoints: PerformanceTouchpoint[];
  goals: PerformanceGoal[];
  schedule: PerformanceSchedule;
  manager: { id: number; name: string; email?: string } | null;
  meName: string;
  teamMode?: boolean;
  onChanged: () => Promise<void> | void;
  openSignal?: number;
}

const toIcsStamp = (value: string) => value.replace(/[-:]/g, '').replace('.000', '');

const downloadIcs = (filename: string, content: string) => {
  const blob = new Blob([content], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const touchpointStatus = (date: string, today: string) => {
  if (date < today) return { label: 'Held', tone: 'success' as const };
  if (date === today) return { label: 'Today', tone: 'warning' as const };
  return { label: 'Upcoming', tone: 'link' as const };
};

export const TouchpointsPanel: React.FC<TouchpointsPanelProps> = ({
  touchpoints,
  goals,
  schedule,
  manager,
  meName,
  teamMode,
  onChanged,
  openSignal = 0,
}) => {
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('10:00');
  const [duration, setDuration] = React.useState('30');
  const [agenda, setAgenda] = React.useState('');
  const [goalIds, setGoalIds] = React.useState<number[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [tried, setTried] = React.useState(false);
  const [justBooked, setJustBooked] = React.useState<PerformanceTouchpoint | null>(null);

  const today = schedule.today;

  React.useEffect(() => {
    if (openSignal > 0 && !teamMode) setOpen(true);
  }, [openSignal, teamMode]);

  React.useEffect(() => {
    if (!open) return;
    const dateValue = new Date();
    dateValue.setDate(dateValue.getDate() + 7);
    setDate(dateValue.toISOString().slice(0, 10));
    setTime('10:00');
    setDuration('30');
    setAgenda('');
    setGoalIds([]);
    setTried(false);
    setJustBooked(null);
  }, [open]);

  const dateError = !date
    ? 'Choose a date'
    : date < today
      ? 'Choose today or a later date'
      : '';

  const outlookUrl = (touchpoint: PerformanceTouchpoint) => {
    const [hours, minutes] = touchpoint.startTime.split(':').map(Number);
    const endMinutes = hours * 60 + minutes + touchpoint.durationMinutes;
    const endTime = `${String(Math.floor(endMinutes / 60) % 24).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
    const subject = `Performance check-in — ${meName} / ${manager?.name || 'Manager'}`;
    const selectedGoals = goals.filter((goal) => touchpoint.goalIds.includes(goal.id));
    const body = [
      touchpoint.agenda || 'Performance check-in',
      selectedGoals.length ? `Goals to discuss:\n${selectedGoals.map((goal) => `• ${goal.title}`).join('\n')}` : '',
      'Booked from Idarah · My Performance',
    ]
      .filter(Boolean)
      .join('\n\n');
    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject,
      startdt: `${touchpoint.date}T${touchpoint.startTime}:00`,
      enddt: `${touchpoint.date}T${endTime}:00`,
      body,
    });
    if (manager?.email) params.set('to', manager.email);
    return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
  };

  const icsContent = (touchpoint: PerformanceTouchpoint) => {
    const [hours, minutes] = touchpoint.startTime.split(':').map(Number);
    const endMinutes = hours * 60 + minutes + touchpoint.durationMinutes;
    const endTime = `${String(Math.floor(endMinutes / 60) % 24).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
    const subject = `Performance check-in — ${meName} / ${manager?.name || 'Manager'}`;
    const selectedGoals = goals.filter((goal) => touchpoint.goalIds.includes(goal.id));
    const description = [
      touchpoint.agenda || 'Performance check-in',
      selectedGoals.length ? `Goals to discuss: ${selectedGoals.map((goal) => goal.title).join('; ')}` : '',
      'Booked from Idarah · My Performance',
    ]
      .filter(Boolean)
      .join('\\n');
    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Idarah//Performance//EN',
      'BEGIN:VEVENT',
      `UID:${touchpoint.id}@idarah`,
      `DTSTART:${toIcsStamp(`${touchpoint.date}T${touchpoint.startTime}:00`)}`,
      `DTEND:${toIcsStamp(`${touchpoint.date}T${endTime}:00`)}`,
      `SUMMARY:${subject}`,
      `DESCRIPTION:${description}`,
      ...(manager?.email
        ? [`ATTENDEE;CN=${manager.name}:mailto:${manager.email}`]
        : []),
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
  };

  const book = async (mode: 'outlook' | 'ics') => {
    setTried(true);
    if (dateError) return;
    setSaving(true);
    try {
      const created = await performanceApi.touchpoints.create({
        scheduleId: schedule.id,
        date,
        startTime: time,
        durationMinutes: Number(duration),
        agenda: agenda.trim(),
        goalIds,
      });
      setJustBooked(created);
      await onChanged();
      if (mode === 'outlook') {
        window.open(outlookUrl(created), '_blank', 'noopener');
      } else {
        downloadIcs('Performance check-in.ics', icsContent(created));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {teamMode ? 'Check-ins with your team' : `Check-ins with ${manager?.name || 'your manager'}`}
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {teamMode
              ? 'Booked by your direct reports. The Outlook invite is on both calendars.'
              : 'Book a check-in and it goes into both calendars. Idarah keeps the record.'}
          </p>
        </div>
        {!teamMode && manager ? (
          <Button onClick={() => setOpen(true)}>
            <CalendarPlus className="mr-2 h-4 w-4" /> Schedule check-in
          </Button>
        ) : null}
      </div>

      {!touchpoints.length ? (
        <PerformanceEmptyState
          title="No check-ins booked"
          body={
            teamMode
              ? 'When your team books a check-in it appears here and in your Outlook calendar.'
              : 'Book a 30-minute check-in to talk through goals and progress.'
          }
          action={
            !teamMode && manager ? (
              <Button variant="outline" onClick={() => setOpen(true)}>
                <CalendarPlus className="mr-2 h-4 w-4" /> Schedule check-in
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-2">
          {touchpoints.map((touchpoint) => {
            const status = touchpointStatus(touchpoint.date, today);
            const selectedGoals = goals.filter((goal) => touchpoint.goalIds.includes(goal.id));
            return (
              <div
                key={touchpoint.id}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="flex min-w-0 gap-3">
                  <div className="grid h-12 w-14 shrink-0 place-items-center rounded-[10px] border border-border bg-muted/40 text-center">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {formatPerformanceDate(touchpoint.date).split(' ')[1]}
                    </span>
                    <span className="text-base font-semibold leading-none tabular-nums">
                      {formatPerformanceDate(touchpoint.date).split(' ')[0]}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold tabular-nums">
                        {touchpoint.startTime} · {touchpoint.durationMinutes} min
                      </span>
                      <PerformancePill tone={status.tone}>{status.label}</PerformancePill>
                      {teamMode && touchpoint.userName ? (
                        <span className="text-xs text-muted-foreground">
                          with {touchpoint.userName}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {touchpoint.agenda || 'No agenda'}
                    </p>
                    {selectedGoals.length ? (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {selectedGoals.map((goal) => (
                          <span
                            key={goal.id}
                            className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
                          >
                            {goal.title}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a href={outlookUrl(touchpoint)} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Outlook
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      downloadIcs(`Performance check-in ${touchpoint.date}.ics`, icsContent(touchpoint))
                    }
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" /> .ics
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule check-in</DialogTitle>
            <DialogDescription>
              {manager ? `With ${manager.name}.` : 'Your manager.'} The invite goes to Outlook;
              Idarah records the agenda and goals.
            </DialogDescription>
          </DialogHeader>

          {justBooked ? (
            <div className="space-y-3">
              <div className="rounded-[10px] border border-success-border bg-success-bg p-3 text-sm text-success">
                Check-in booked for {formatPerformanceDate(justBooked.date)} at{' '}
                {justBooked.startTime}. Use the buttons below if the calendar did not open.
              </div>
              <div className="flex flex-wrap gap-2">
                <Button asChild>
                  <a href={outlookUrl(justBooked)} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" /> Open in Outlook
                  </a>
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    downloadIcs('Performance check-in.ics', icsContent(justBooked))
                  }
                >
                  <Download className="mr-2 h-4 w-4" /> Download .ics
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="touchpoint-date">Date</Label>
                  <Input
                    id="touchpoint-date"
                    type="date"
                    min={today}
                    value={date}
                    onChange={(event) => setDate(event.target.value)}
                  />
                  {tried && dateError ? (
                    <p className="text-xs text-destructive">{dateError}</p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="touchpoint-time">Start time</Label>
                  <Input
                    id="touchpoint-time"
                    type="time"
                    value={time}
                    onChange={(event) => setTime(event.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Duration</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">60 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="touchpoint-agenda">Agenda</Label>
                <Textarea
                  id="touchpoint-agenda"
                  value={agenda}
                  onChange={(event) => setAgenda(event.target.value)}
                  rows={3}
                  placeholder="What do you want to talk through?"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Goals to discuss</Label>
                <div className="flex flex-wrap gap-1.5">
                  {goals
                    .filter((goal) => goal.status !== 'Withdrawn')
                    .map((goal) => {
                      const selected = goalIds.includes(goal.id);
                      return (
                        <button
                          key={goal.id}
                          type="button"
                          onClick={() =>
                            setGoalIds((current) =>
                              current.includes(goal.id)
                                ? current.filter((id) => id !== goal.id)
                                : [...current, goal.id]
                            )
                          }
                          className={cn(
                            'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                            selected
                              ? 'border-link/40 bg-selection text-link'
                              : 'border-border bg-card text-foreground hover:bg-accent'
                          )}
                        >
                          {goal.title}
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {justBooked ? (
              <Button variant="outline" onClick={() => setOpen(false)}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button variant="outline" onClick={() => book('ics')} disabled={saving}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  Book & .ics
                </Button>
                <Button onClick={() => book('outlook')} disabled={saving}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                  Book in Outlook
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
